import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircleIcon } from "lucide-react";
import BrandAddForm from "@/polymet/components/brand-add-form";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth-context";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuthDebug } from "@/lib/auth-debug";

export default function AddBrandPage() {
  const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Debug auth context changes
  useAuthDebug("AddBrandPage");
  
  console.log('🔄 AddBrandPage render - user:', user?.id);
  
  useEffect(() => {
    // Check if user is logged in and has supplier privileges
    const checkAuthorization = async () => {
      setLoading(true);
      
      if (!user) {
        setIsAuthorized(false);
        setLoading(false);
        return;
      }
      
      try {
        // Check if the user is a supplier
        const { data: profile, error: profileError } = await supabase
          .from('accounts')
          .select('user_type')
          .eq('id', user.id)
          .single();
        
        if (profileError) throw profileError;
        
        // Only suppliers or users with both roles can add brands
        setIsAuthorized(profile?.user_type === 'supplier' || profile?.user_type === 'both');
      } catch (error) {
        console.error('Error checking authorization:', error);
        setIsAuthorized(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthorization();
  }, [user]);
  
  // Redirect unauthorized users
  useEffect(() => {
    if (!loading && !isAuthorized && !user) {
      navigate('/login?redirect=/add-brand');
    }
  }, [loading, isAuthorized, user, navigate]);
  
  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }
  
  if (!isAuthorized && user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive" className="mb-6">
          <AlertCircleIcon className="h-4 w-4" />
          <AlertDescription>
            You must be a registered supplier to add brands. Please contact support if you believe this is an error.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold">Add Brand</h1>
          <p className="text-muted-foreground">
            Add a new machinery brand to your catalog
          </p>
        </div>
      </div>

      <Card className="mb-8">
        <CardHeader className="pb-3">
          <CardTitle>Brand Information</CardTitle>
          <CardDescription>
            Enter the details for the new brand
          </CardDescription>
        </CardHeader>
        <CardContent>
          <BrandAddForm />
        </CardContent>
      </Card>
    </div>
  );
} 